import { MapPin, Instagram, Clock, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function AboutSection() {
  return (
    <section id="about" className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div 
          className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(220, 20, 60, 0.4) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(255, 215, 0, 0.4) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <Badge className="mb-4 bg-[#DC143C]/10 text-[#DC143C] border-[#DC143C]/30">
              About Us
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 font-['Orbitron']">
              <span className="text-white">Bringing Seoul to</span>{' '}
              <span className="gradient-text">Kissimmee</span>
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                KPC Urban Korean Food was born from a passion for authentic Korean urban food 
                and a desire to share the vibrant flavors of Seoul with our community. Our 
                recipes are inspired by the bustling night markets of Myeongdong and the 
                trendy food scene of Hongdae.
              </p>
              <p>
                Every dish we serve is crafted with care, using traditional Korean ingredients 
                and techniques combined with a modern urban twist. From our signature K-Bomb 
                Fried Chicken to our unique Tater Toast creations, we bring you an authentic 
                taste of Korea right here in Florida.
              </p>
              <p>
                Whether you are craving the comfort of bibimbap or the crunch of Korean fried 
                chicken, we have got something to satisfy your Seoul food cravings!
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FFD700] font-['Orbitron']">20+</div>
                <div className="text-sm text-muted-foreground">Menu Items</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#DC143C] font-['Orbitron']">100%</div>
                <div className="text-sm text-muted-foreground">Authentic</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FFA500] font-['Orbitron']">15min</div>
                <div className="text-sm text-muted-foreground">Pickup</div>
              </div>
            </div>
          </div>

          {/* Right Content - Contact Info */}
          <div id="contact" className="space-y-6">
            <div className="glass-card rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 font-['Orbitron'] text-white">
                Visit Us
              </h3>
              
              <div className="space-y-6">
                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Location</p>
                    <p className="text-muted-foreground">
                      6083 W Irlo Bronson Memorial Hwy<br />
                      Kissimmee, FL 34747
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#DC143C]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#DC143C]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Hours</p>
                    <p className="text-muted-foreground">
                      Monday - Sunday<br />
                      11:00 AM - 10:00 PM
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FFA500]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FFA500]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Phone</p>
                    <a href="tel:4075070287" className="text-[#FFD700] hover:underline">
                      (407) 507-0287
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD700]/10 to-[#DC143C]/10 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Instagram</p>
                    <a 
                      href="https://instagram.com/kpc.urbankoreanfood"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#FFD700] hover:underline"
                    >
                      @kpc.urbankoreanfood
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="glass-card rounded-2xl p-1 overflow-hidden">
              <div className="aspect-video rounded-xl bg-gradient-to-br from-[#FFD700]/5 to-[#DC143C]/5 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-[#FFD700] mx-auto mb-2" />
                  <p className="text-muted-foreground">6083 W Irlo Bronson Memorial Hwy</p>
                  <p className="text-sm text-muted-foreground">Kissimmee, FL 34747</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
